import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  GraduationCap, 
  Plus, 
  Search,
  Loader2,
  BookOpen,
  MapPin,
  X
} from 'lucide-react';

const COLORS = [
  { value: '#3b82f6', name: 'Blue' },
  { value: '#10b981', name: 'Green' },
  { value: '#f59e0b', name: 'Amber' },
  { value: '#ef4444', name: 'Red' },
  { value: '#8b5cf6', name: 'Purple' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#6366f1', name: 'Indigo' },
  { value: '#14b8a6', name: 'Teal' },
];

export default function SubjectSelector({ 
  currentSubjectId, 
  onSelect,
  showTriggerDot = true 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // New subject form
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newCourseCode, setNewCourseCode] = useState('');
  const [newColor, setNewColor] = useState('#3b82f6');
  
  // University search
  const [universityQuery, setUniversityQuery] = useState('');
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [searchingUniversities, setSearchingUniversities] = useState(false);

  const searchUniversities = useCallback(async () => {
    setSearchingUniversities(true);
    try {
      const data = await api.searchUniversities(universityQuery);
      setUniversities(data.universities || []);
    } catch (error) {
      console.error('Failed to search universities:', error);
    } finally {
      setSearchingUniversities(false);
    }
  }, [universityQuery]);

  useEffect(() => {
    if (isOpen) {
      loadSubjects();
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (universityQuery.length >= 2) {
        searchUniversities();
      } else {
        setUniversities([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [universityQuery, searchUniversities]);

  const loadSubjects = async () => {
    try {
      const data = await api.getSubjects();
      setSubjects(data);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const handleCreateSubject = async () => {
    if (!newSubjectName.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    setLoading(true);
    try {
      const subject = await api.createSubject({
        name: newSubjectName,
        color: newColor,
        description: newCourseCode ? `${newCourseCode}${selectedUniversity ? ` at ${selectedUniversity.name}` : ''}` : undefined
      });
      
      setSubjects([...subjects, subject]);
      onSelect(subject.id);
      toast.success('Subject created');
      resetForm();
      setIsOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to create subject');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSubject = (subjectId) => {
    onSelect(subjectId);
    setIsOpen(false);
  };

  const resetForm = () => {
    setNewSubjectName('');
    setNewCourseCode('');
    setNewColor('#3b82f6');
    setUniversityQuery('');
    setSelectedUniversity(null);
    setIsCreating(false);
  };

  const currentSubject = subjects.find(s => s.id === currentSubjectId);

  return (
    <>
      {/* Yellow Dot Trigger */}
      {showTriggerDot && (
        <button
          onClick={() => setIsOpen(true)}
          className={`relative flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
            currentSubjectId 
              ? 'bg-muted hover:bg-muted/80' 
              : 'bg-amber-500/20 hover:bg-amber-500/30 animate-pulse'
          }`}
          data-testid="subject-selector-trigger"
        >
          {!currentSubjectId && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-500 border-2 border-background" />
          )}
          <GraduationCap className="w-4 h-4" />
          <span className={currentSubjectId ? '' : 'text-amber-600'}>
            {currentSubject?.name || 'Add Subject'}
          </span>
        </button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              {isCreating ? 'Create Subject' : 'Select Subject'}
            </DialogTitle>
            <DialogDescription>
              {isCreating 
                ? 'Add a new subject to organize your lectures'
                : 'Choose a subject for this lecture'
              }
            </DialogDescription>
          </DialogHeader>

          {isCreating ? (
            <div className="space-y-4">
              {/* Subject Name */}
              <div>
                <Label htmlFor="subject-name">Subject Name</Label>
                <Input
                  id="subject-name"
                  value={newSubjectName}
                  onChange={(e) => setNewSubjectName(e.target.value)}
                  placeholder="e.g., Introduction to Psychology"
                  className="mt-1.5"
                  data-testid="new-subject-name"
                />
              </div>

              {/* Course Code */}
              <div>
                <Label htmlFor="course-code">Course Code (optional)</Label>
                <Input
                  id="course-code"
                  value={newCourseCode}
                  onChange={(e) => setNewCourseCode(e.target.value)}
                  placeholder="e.g., PSY101"
                  className="mt-1.5"
                  data-testid="new-course-code"
                />
              </div>

              {/* University Search */}
              <div>
                <Label>University (optional)</Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={selectedUniversity ? selectedUniversity.name : universityQuery}
                    onChange={(e) => {
                      setUniversityQuery(e.target.value);
                      setSelectedUniversity(null);
                    }}
                    placeholder="Search university..."
                    className="pl-9"
                    data-testid="university-search"
                  />
                  {selectedUniversity && (
                    <button
                      onClick={() => {
                        setSelectedUniversity(null);
                        setUniversityQuery('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                
                {/* University Results */}
                {universities.length > 0 && !selectedUniversity && (
                  <ScrollArea className="mt-2 h-40 border rounded-lg">
                    <div className="p-2 space-y-1">
                      {universities.map((uni, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedUniversity(uni);
                            setUniversities([]);
                          }}
                          className="w-full text-left p-2 rounded-lg hover:bg-muted transition-colors"
                        >
                          <p className="font-medium text-sm">{uni.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {uni.location}
                          </p>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                
                {searchingUniversities && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </div>
                )}
              </div>

              {/* Color Selection */}
              <div>
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewColor(color.value)}
                      className={`w-8 h-8 rounded-full transition-transform ${
                        newColor === color.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateSubject} disabled={loading} className="flex-1">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Existing Subjects */}
              {subjects.length > 0 ? (
                <ScrollArea className="h-60">
                  <div className="space-y-2 pr-4">
                    {/* No subject option */}
                    <button
                      onClick={() => handleSelectSubject(null)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                        !currentSubjectId ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium">No Subject</p>
                        <p className="text-xs text-muted-foreground">General lecture</p>
                      </div>
                    </button>
                    
                    {subjects.map((subject) => (
                      <button
                        key={subject.id}
                        onClick={() => handleSelectSubject(subject.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          currentSubjectId === subject.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                        }`}
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${subject.color}20` }}
                        >
                          <GraduationCap className="w-5 h-5" style={{ color: subject.color }} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium">{subject.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {subject.lecture_count} lecture{subject.lecture_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No subjects yet</p>
                </div>
              )}

              {/* Create New Button */}
              <Button 
                variant="outline" 
                onClick={() => setIsCreating(true)}
                className="w-full"
                data-testid="create-subject-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Subject
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
